/**
 * PermanentDetail — fiche détaillée d'un permanent indépendant.
 *
 * - Bloc identité + bouton "modifier"
 * - Timeline des contrats (nouveau en haut)
 * - Bouton "Renouveler" sur le contrat le plus récent (dates flexibles)
 * - Boutons "Télécharger PDF" + "Uploader PDF signé" sur les contrats draft
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Copy,
    Download,
    FileSignature,
    Loader2,
    Mail,
    Pencil,
    Plus,
    RefreshCw,
    Send,
    Upload,
    UserCircle2,
    X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import RenewContractModal from "../components/permanents/RenewContractModal";
import EditPermanentModal from "../components/permanents/EditPermanentModal";
import NewAvenantModal from "../components/permanents/NewAvenantModal";
import PermanentBadgeSection from "../components/permanents/PermanentBadgeSection";


function fmt(iso) {
    if (!iso) return "—";
    return iso;
}


export default function PermanentDetail() {
    const { pid } = useParams();
    const [loading, setLoading] = useState(true);
    const [permanent, setPermanent] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [avenants, setAvenants] = useState([]);
    const [renewOpen, setRenewOpen] = useState(false);
    const [renewSource, setRenewSource] = useState(null);
    const [avenantOpen, setAvenantOpen] = useState(false);
    const [avenantSource, setAvenantSource] = useState(null);
    const [uploadingCid, setUploadingCid] = useState(null);
    const [uploadingAid, setUploadingAid] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [regeneratingCid, setRegeneratingCid] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/permanents/${pid}`);
            setPermanent(data.permanent);
            setContracts(data.contracts || []);
        } catch (e) {
            toast.error("Chargement impossible : " + (e?.response?.data?.detail || e.message));
        }
        try {
            const { data: av } = await api.get(`/permanents/${pid}/avenants`);
            setAvenants(av.items || []);
        } catch {
            // liste avenants non bloquante — vide par défaut
            setAvenants([]);
        } finally {
            setLoading(false);
        }
    }, [pid]);

    useEffect(() => { load(); }, [load]);

    async function downloadPdf(cid, signed = false) {
        try {
            const res = await api.get(`/permanents/${pid}/contracts/${cid}/pdf`, {
                params: signed ? { signed: 1 } : {},
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `contrat-${cid.slice(0, 8)}${signed ? "-signe" : ""}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Téléchargement impossible : " + (e?.response?.data?.detail || e.message));
        }
    }

    async function handleSignedUpload(cid, file) {
        if (!file) return;
        setUploadingCid(cid);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/permanents/${pid}/contracts/${cid}/sign`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("PDF signé importé");
            load();
        } catch (e) {
            toast.error("Import impossible : " + (e?.response?.data?.detail || e.message));
        } finally {
            setUploadingCid(null);
        }
    }

    async function handleResendEmail(cid) {
        try {
            const { data } = await api.post(`/permanents/${pid}/contracts/${cid}/send-email`);
            if (data.ok) {
                toast.success(`Contrat envoyé à ${data.to} (envoi n°${data.send_count})`);
            } else {
                toast.error(`Envoi échoué : ${data.error || "voir logs"}`);
            }
            load();
        } catch (e) {
            toast.error("Envoi impossible : " + (e?.response?.data?.detail || e.message));
        }
    }

    async function regenerateContractPdf(cid) {
        const ok = window.confirm(
            "Régénérer le PDF de ce contrat depuis le template actuel ?\n\n" +
            "→ Prend en compte les modifications de la fiche permanent (civilité, adresse…)\n" +
            "→ Écrase le PDF précédent, même URL\n" +
            "→ Impossible sur un contrat déjà signé"
        );
        if (!ok) return;
        setRegeneratingCid(cid);
        try {
            await api.post(`/permanents/${pid}/contracts/${cid}/regenerate`);
            toast.success("PDF régénéré · télécharger le nouveau pour vérifier");
            await load();
        } catch (e) {
            toast.error("Régénération impossible : " + (e?.response?.data?.detail || e.message));
        } finally {
            setRegeneratingCid(null);
        }
    }

    async function downloadAvenantPdf(aid, signed = false) {
        try {
            const res = await api.get(`/permanents/${pid}/avenants/${aid}/pdf`, {
                params: signed ? { signed: 1 } : {},
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `avenant-${aid.slice(0, 8)}${signed ? "-signe" : ""}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Téléchargement impossible : " + (e?.response?.data?.detail || e.message));
        }
    }

    async function handleAvenantResendEmail(aid) {
        try {
            const { data } = await api.post(`/permanents/${pid}/avenants/${aid}/send-email`);
            if (data.ok) {
                toast.success(`Avenant envoyé à ${data.to} (envoi n°${data.send_count})`);
            } else {
                toast.error(`Envoi échoué : ${data.error || "voir logs"}`);
            }
            load();
        } catch (e) {
            toast.error("Envoi impossible : " + (e?.response?.data?.detail || e.message));
        }
    }

    async function handleAvenantSignedUpload(aid, file) {
        if (!file) return;
        setUploadingAid(aid);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/permanents/${pid}/avenants/${aid}/sign`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Avenant signé importé");
            load();
        } catch (e) {
            toast.error("Import impossible : " + (e?.response?.data?.detail || e.message));
        } finally {
            setUploadingAid(null);
        }
    }


    if (loading) {
        return <div className="p-8 text-center text-slate-400 text-[13px]" data-testid="permanent-detail-loading">Chargement…</div>;
    }
    if (!permanent) {
        return <div className="p-8 text-center text-slate-400 text-[13px]">Permanent introuvable</div>;
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="permanent-detail-page">
            <Link to="/permanents" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-slate-500 hover:text-shariti-orange transition">
                <ArrowLeft className="h-3.5 w-3.5" /> Retour à la liste
            </Link>

            {/* Identity card */}
            <div className="card-soft p-6" data-testid="permanent-identity-card">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="h-14 w-14 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                        <UserCircle2 className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <h1 className="text-xl font-black text-shariti-black" data-testid="permanent-header-name">
                                {permanent.civilite} {permanent.first_name} {permanent.last_name}
                                {(permanent.prenom_usage || permanent.nom_usage) && (
                                    <span
                                        className="ml-2 text-base font-bold text-slate-400"
                                        data-testid="permanent-header-usage"
                                        title="Nom d'usage terrain — utilisé pour les plannings et Evergiving. Les documents légaux (contrats, PayFit) restent sur le nom légal."
                                    >
                                        ({permanent.prenom_usage || permanent.first_name} {permanent.nom_usage || permanent.last_name})
                                    </span>
                                )}
                            </h1>
                            <button
                                type="button"
                                onClick={() => setEditOpen(true)}
                                data-testid="permanent-edit-btn"
                                className="inline-flex items-center gap-1.5 rounded-full border border-shariti-orange bg-white hover:bg-shariti-orange-soft text-shariti-orange px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition"
                            >
                                <Pencil className="h-3 w-3" /> Modifier la fiche
                            </button>
                        </div>
                        <div className="text-[12px] text-slate-500 mt-1">
                            {permanent.email}{permanent.phone ? ` · ${permanent.phone}` : ""}
                            {permanent.code_rd && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(permanent.code_rd);
                                            toast.success(`Code ${permanent.code_rd} copié`);
                                        } catch {
                                            toast.error("Copie impossible");
                                        }
                                    }}
                                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-shariti-orange-soft border border-shariti-orange/40 text-shariti-orange px-2 py-0.5 text-[10px] font-black uppercase tracking-widest align-middle hover:bg-shariti-orange hover:text-white transition"
                                    data-testid="permanent-code-rd-pill"
                                    title="Code recruteur Shariti — cliquer pour copier"
                                >
                                    <span className="mono">{permanent.code_rd}</span>
                                    <Copy className="h-2.5 w-2.5 opacity-70" />
                                </button>
                            )}
                        </div>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-[12px]">
                            <MetaTile label="SIRET" value={permanent.siret} mono />
                            <MetaTile label="Statut fiscal" value={permanent.legal_status} />
                            <MetaTile label="Région" value={(permanent.region || "").toUpperCase()} />
                            <MetaTile label="Adresse" value={`${permanent.address_line1}, ${permanent.address_postal_code} ${permanent.address_city}`} full />
                            {permanent.birth_date && <MetaTile label="Date de naissance" value={permanent.birth_date} />}
                        </div>

                        <RemunerationEditor
                            permanent={permanent}
                            onSaved={load}
                        />

                        <UsageEditor
                            permanent={permanent}
                            onSaved={load}
                        />
                    </div>
                </div>
            </div>

            {/* Contracts — timeline */}
            <div className="card-soft p-6" data-testid="permanent-contracts-card">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-lg font-black text-shariti-black">Timeline des contrats</h2>
                        <p className="text-[12px] text-slate-500">
                            {contracts.length} contrat{contracts.length > 1 ? "s" : ""} enregistré{contracts.length > 1 ? "s" : ""}
                            {contracts.length > 1 && " · plus récent en haut"}
                        </p>
                    </div>
                    {contracts.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap" data-testid="permanent-contract-actions">
                            <button
                                onClick={() => { setAvenantSource(contracts[0]); setAvenantOpen(true); }}
                                data-testid="permanent-new-avenant-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-shariti-orange bg-white text-shariti-orange px-4 py-2 text-[11px] font-black uppercase tracking-widest hover:bg-shariti-orange-soft transition"
                            >
                                <FileSignature className="h-3.5 w-3.5" /> Nouvel avenant
                            </button>
                            <button
                                onClick={() => { setRenewSource(contracts[0]); setRenewOpen(true); }}
                                data-testid="permanent-renew-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white px-4 py-2 text-[11px] font-black uppercase tracking-widest hover:opacity-90 transition"
                            >
                                <RefreshCw className="h-3.5 w-3.5" /> Renouveler
                            </button>
                        </div>
                    )}
                </div>

                {contracts.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-[13px]" data-testid="permanent-no-contracts">
                        Aucun contrat pour ce permanent. Créer via la modale principale (Nouveau permanent).
                    </div>
                ) : (
                    <div className="relative pl-7" data-testid="permanent-contracts-timeline">
                        {/* Vertical rail */}
                        <div className="absolute left-2.5 top-2 bottom-2 w-px bg-gradient-to-b from-shariti-orange via-slate-200 to-slate-100" aria-hidden />
                        <div className="space-y-4">
                            {contracts.map((c, idx) => {
                                const isCurrent = idx === 0;
                                const contractNum = contracts.length - idx;   // oldest = 1
                                const previous = c.previous_contract_id
                                    ? contracts.find((x) => x.id === c.previous_contract_id)
                                    : null;
                                return (
                                    <div key={c.id} className="relative" data-testid={`permanent-contract-${c.id}`}>
                                        {/* Timeline dot */}
                                        <span
                                            className={`absolute -left-[26px] top-4 h-3.5 w-3.5 rounded-full ring-4 ${
                                                isCurrent
                                                    ? "bg-shariti-orange ring-shariti-orange-soft"
                                                    : c.status === "expired"
                                                        ? "bg-slate-300 ring-slate-100"
                                                        : c.status === "signed"
                                                            ? "bg-emerald-500 ring-emerald-100"
                                                            : "bg-slate-400 ring-slate-100"
                                            }`}
                                            aria-hidden
                                        />
                                        {/* Year chip (only when it changes) */}
                                        {(idx === 0 || (contracts[idx - 1]?.start_date || "").slice(0, 4) !== (c.start_date || "").slice(0, 4)) && (
                                            <div className="absolute -left-[62px] top-3 hidden md:block text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                {(c.start_date || "").slice(0, 4)}
                                            </div>
                                        )}
                                        <div className={`rounded-2xl border p-4 ${isCurrent ? "border-shariti-orange bg-shariti-orange-soft/30" : "border-slate-200 bg-white"}`}>
                                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                                            {isCurrent ? "Contrat actuel" : `Contrat #${contractNum}`}
                                                        </span>
                                                        <StatusPill status={c.status} />
                                                        <OriginPill origin={c.origin} />
                                                        {previous && (
                                                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest">
                                                                <RefreshCw className="h-2.5 w-2.5" /> Renouvelé depuis #{contracts.length - contracts.findIndex(x => x.id === previous.id)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-1.5 text-[14px] font-bold text-shariti-black">
                                                        {fmt(c.start_date)} → {fmt(c.end_date)}
                                                    </div>
                                                    <div className="text-[11px] text-slate-500">
                                                        Zone : {c.primary_region || "—"} · Min. mensuel : {c.monthly_minimum_bulletins || 30} bulletins ·
                                                        Cadence : {c.duration_kind}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {c.pdf_generated_path && (
                                                        <button
                                                            onClick={() => downloadPdf(c.id, false)}
                                                            data-testid={`contract-download-${c.id}`}
                                                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 transition"
                                                        >
                                                            <Download className="h-3 w-3" /> PDF généré
                                                        </button>
                                                    )}
                                                    {c.pdf_generated_path && !c.pdf_signed_path && c.origin === "generated" && (
                                                        <button
                                                            onClick={() => regenerateContractPdf(c.id)}
                                                            disabled={regeneratingCid === c.id}
                                                            data-testid={`contract-regenerate-${c.id}`}
                                                            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 px-3 py-1.5 text-[11px] font-bold hover:bg-amber-100 transition disabled:opacity-50"
                                                            title="Ré-génère le PDF depuis le template actuel + données à jour du permanent. À utiliser après modification de la civilité, de l'adresse, etc."
                                                        >
                                                            {regeneratingCid === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                                            Régénérer le PDF
                                                        </button>
                                                    )}
                                                    {c.pdf_generated_path && !c.pdf_signed_path && c.origin === "generated" && (
                                                        <button
                                                            onClick={() => handleResendEmail(c.id)}
                                                            data-testid={`contract-send-email-${c.id}`}
                                                            className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 px-3 py-1.5 text-[11px] font-bold hover:bg-blue-100 transition"
                                                            title={c.sent_at
                                                                ? `Dernier envoi : ${new Date(c.sent_at).toLocaleString('fr-FR')}${c.send_count ? ` (envoi n°${c.send_count})` : ''}`
                                                                : "Envoyer le PDF par email au prestataire pour signature"}
                                                        >
                                                            {c.sent_at ? <Send className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                                                            {c.sent_at ? "Renvoyer" : "Envoyer par email"}
                                                        </button>
                                                    )}
                                                    {c.pdf_signed_path && (
                                                        <button
                                                            onClick={() => downloadPdf(c.id, true)}
                                                            data-testid={`contract-download-signed-${c.id}`}
                                                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 text-[11px] font-bold hover:bg-emerald-100 transition"
                                                        >
                                                            <FileSignature className="h-3 w-3" /> PDF signé
                                                        </button>
                                                    )}
                                                    {!c.pdf_signed_path && c.origin === "generated" && (
                                                        <label className="inline-flex items-center gap-1.5 rounded-full bg-white border border-shariti-orange text-shariti-orange px-3 py-1.5 text-[11px] font-bold hover:bg-shariti-orange-soft cursor-pointer transition">
                                                            {uploadingCid === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                                            Uploader PDF signé
                                                            <input
                                                                type="file"
                                                                accept="application/pdf"
                                                                className="hidden"
                                                                data-testid={`contract-upload-signed-${c.id}`}
                                                                disabled={uploadingCid === c.id}
                                                                onChange={(e) => handleSignedUpload(c.id, e.target.files?.[0])}
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Avenants — timeline dédiée */}
            <AvenantsSection
                avenants={avenants}
                contracts={contracts}
                onDownload={downloadAvenantPdf}
                onResend={handleAvenantResendEmail}
                onUpload={handleAvenantSignedUpload}
                uploadingAid={uploadingAid}
                onNewAvenant={() => {
                    if (!contracts.length) {
                        toast.error("Créez d'abord un contrat de base");
                        return;
                    }
                    setAvenantSource(contracts[0]);
                    setAvenantOpen(true);
                }}
            />

            {/* Badge médiateur */}
            <PermanentBadgeSection
                pid={pid}
                permanent={permanent}
                hasSignedContract={contracts.some((c) => c.status === "signed" || c.status === "active")}
                onSaved={load}
            />

            {renewOpen && renewSource && (
                <RenewContractModal
                    pid={pid}
                    source={renewSource}
                    onClose={() => setRenewOpen(false)}
                    onDone={() => { setRenewOpen(false); load(); }}
                />
            )}

            {avenantOpen && avenantSource && (
                <NewAvenantModal
                    pid={pid}
                    contract={avenantSource}
                    onClose={() => setAvenantOpen(false)}
                    onDone={() => { setAvenantOpen(false); load(); }}
                />
            )}

            {editOpen && (
                <EditPermanentModal
                    pid={pid}
                    permanent={permanent}
                    onClose={() => setEditOpen(false)}
                    onDone={() => { setEditOpen(false); load(); }}
                />
            )}
        </div>
    );
}


function MetaTile({ label, value, mono = false, full = false }) {
    return (
        <div className={full ? "col-span-2 md:col-span-3" : ""}>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">{label}</div>
            <div className={`text-[13px] font-bold text-shariti-black ${mono ? "mono" : ""}`}>{value || "—"}</div>
        </div>
    );
}


function StatusPill({ status }) {
    const map = {
        draft: { tone: "bg-slate-100 text-slate-700", label: "Brouillon" },
        signed: { tone: "bg-emerald-100 text-emerald-700", label: "Signé" },
        active: { tone: "bg-blue-100 text-blue-700", label: "Actif" },
        expired: { tone: "bg-red-100 text-red-700", label: "Expiré" },
        cancelled: { tone: "bg-slate-200 text-slate-500 line-through", label: "Rompu" },
    };
    const meta = map[status] || map.draft;
    return <span className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${meta.tone}`}>{meta.label}</span>;
}


function OriginPill({ origin }) {
    return (
        <span className="inline-block rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest">
            {origin === "uploaded" ? "Import signé" : "Généré PREREC"}
        </span>
    );
}


function AvenantStatusPill({ status }) {
    const map = {
        draft: { tone: "bg-slate-100 text-slate-700", label: "Brouillon" },
        sent: { tone: "bg-blue-100 text-blue-700", label: "Envoyé" },
        opened: { tone: "bg-amber-100 text-amber-700", label: "Ouvert" },
        signed: { tone: "bg-emerald-100 text-emerald-700", label: "Signé" },
        cancelled: { tone: "bg-slate-200 text-slate-500 line-through", label: "Annulé" },
    };
    const meta = map[status] || map.draft;
    return <span className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${meta.tone}`}>{meta.label}</span>;
}


function AvenantsSection({ avenants, contracts, onDownload, onResend, onUpload, uploadingAid, onNewAvenant }) {
    // Retrouve le libellé du contrat de base pour chaque avenant (période).
    const contractById = new Map((contracts || []).map((c) => [c.id, c]));

    return (
        <div className="card-soft p-6" data-testid="permanent-avenants-card">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <h2 className="text-lg font-black text-shariti-black">Avenants</h2>
                    <p className="text-[12px] text-slate-500">
                        {avenants.length === 0
                            ? "Aucun avenant émis pour ce prestataire."
                            : `${avenants.length} avenant${avenants.length > 1 ? "s" : ""} · plus récent en haut`}
                    </p>
                </div>
                <button
                    onClick={onNewAvenant}
                    data-testid="permanent-new-avenant-btn-section"
                    className="inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white px-4 py-2 text-[11px] font-black uppercase tracking-widest hover:opacity-90 transition"
                >
                    <Plus className="h-3.5 w-3.5" /> Créer un avenant
                </button>
            </div>

            {avenants.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-[12px] text-slate-500" data-testid="permanent-no-avenants">
                    Modèle CEO disponible : <b>Ouverture et animation de zone</b> — forfait 1&nbsp;150&nbsp;€/mois,
                    grille 35&nbsp;/&nbsp;50&nbsp;/&nbsp;65&nbsp;€, commission de coordination 10&nbsp;€/bulletin,
                    retenue de garantie 20&nbsp;%. Cliquez sur <b>Créer un avenant</b> pour l&apos;émettre en 1 clic.
                </div>
            ) : (
                <div className="space-y-3">
                    {avenants.map((a) => {
                        const baseContract = contractById.get(a.contract_id);
                        return (
                            <div key={a.id} className="rounded-2xl border border-shariti-orange/40 bg-shariti-orange-soft/20 p-4" data-testid={`permanent-avenant-${a.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-shariti-orange">
                                                Avenant N°{a.number}
                                            </span>
                                            <AvenantStatusPill status={a.status} />
                                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest">
                                                Ouverture zone {a.zone}
                                            </span>
                                        </div>
                                        <div className="mt-1.5 text-[14px] font-bold text-shariti-black">
                                            {a.effective_date} → {a.end_date}
                                        </div>
                                        <div className="text-[11px] text-slate-500">
                                            Forfait 1&nbsp;150&nbsp;€/mois · Commission 10&nbsp;€/bulletin coord · Retenue 20%
                                            {baseContract && (
                                                <> · Contrat de base : <span className="mono">{baseContract.id.slice(0, 8).toUpperCase()}</span></>
                                            )}
                                            {a.sent_at && <> · Envoyé le {new Date(a.sent_at).toLocaleString("fr-FR")}</>}
                                            {a.signed_at && <> · Signé le {new Date(a.signed_at).toLocaleString("fr-FR")}</>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {a.pdf_generated_path && (
                                            <button
                                                onClick={() => onDownload(a.id, false)}
                                                data-testid={`avenant-download-${a.id}`}
                                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 transition"
                                            >
                                                <Download className="h-3 w-3" /> PDF généré
                                            </button>
                                        )}
                                        {a.pdf_generated_path && !a.pdf_signed_path && (
                                            <button
                                                onClick={() => onResend(a.id)}
                                                data-testid={`avenant-send-email-${a.id}`}
                                                className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 px-3 py-1.5 text-[11px] font-bold hover:bg-blue-100 transition"
                                                title={a.sent_at
                                                    ? `Dernier envoi : ${new Date(a.sent_at).toLocaleString("fr-FR")}${a.send_count ? ` (envoi n°${a.send_count})` : ""}`
                                                    : "Envoyer l'avenant par email au prestataire pour signature"}
                                            >
                                                {a.sent_at ? <Send className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                                                {a.sent_at ? "Renvoyer" : "Envoyer par email"}
                                            </button>
                                        )}
                                        {a.pdf_signed_path && (
                                            <button
                                                onClick={() => onDownload(a.id, true)}
                                                data-testid={`avenant-download-signed-${a.id}`}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 text-[11px] font-bold hover:bg-emerald-100 transition"
                                            >
                                                <FileSignature className="h-3 w-3" /> PDF signé
                                            </button>
                                        )}
                                        {!a.pdf_signed_path && (
                                            <label className="inline-flex items-center gap-1.5 rounded-full bg-white border border-shariti-orange text-shariti-orange px-3 py-1.5 text-[11px] font-bold hover:bg-shariti-orange-soft cursor-pointer transition">
                                                {uploadingAid === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                                Uploader PDF signé
                                                <input
                                                    type="file"
                                                    accept="application/pdf"
                                                    className="hidden"
                                                    data-testid={`avenant-upload-signed-${a.id}`}
                                                    disabled={uploadingAid === a.id}
                                                    onChange={(e) => onUpload(a.id, e.target.files?.[0])}
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}



/* ─────────────────────── iter69 editors ─────────────────────── */

function RemunerationEditor({ permanent, onSaved }) {
    const [kind, setKind] = useState(permanent.remuneration?.kind || "forfait_mensuel");
    const [montant, setMontant] = useState(permanent.remuneration?.montant?.toString() || "");
    const [saving, setSaving] = useState(false);
    const dirty =
        kind !== (permanent.remuneration?.kind || "forfait_mensuel") ||
        (montant || "") !== (permanent.remuneration?.montant?.toString() || "");

    async function save() {
        const parsed = parseFloat(montant);
        if (isNaN(parsed) || parsed < 0) {
            toast.error("Montant invalide");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/permanents/${permanent.id}`, {
                remuneration: { kind, montant: parsed },
            });
            toast.success("Rémunération enregistrée");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sauvegarde échouée");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="mt-4 rounded-2xl border border-shariti-orange/40 bg-shariti-orange-soft/20 p-4" data-testid="permanent-remuneration-block">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange">
                        Rémunération de pilotage
                    </div>
                    <div className="text-[11px] text-slate-600 leading-snug">
                        Valeur portée par le prestataire (utilisée par Reporting). La
                        grille bulletins du template PDF reste inchangée.
                    </div>
                </div>
                {permanent.remuneration && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-700" data-testid="permanent-remuneration-current">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {permanent.remuneration.kind === "forfait_mensuel"
                            ? `Forfait ${permanent.remuneration.montant} €/mois`
                            : `Taux ${permanent.remuneration.montant} €/bulletin`}
                    </span>
                )}
            </div>
            <div className="grid grid-cols-3 gap-2">
                <select
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                    data-testid="permanent-remuneration-kind"
                    className="col-span-2 md:col-span-1 rounded-lg border border-shariti-border bg-white px-3 py-1.5 text-[12px] font-bold focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange"
                >
                    <option value="forfait_mensuel">Forfait mensuel</option>
                    <option value="taux_bulletin">Taux au bulletin</option>
                </select>
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={montant}
                    onChange={(e) => setMontant(e.target.value)}
                    placeholder={kind === "forfait_mensuel" ? "3500" : "80"}
                    data-testid="permanent-remuneration-montant"
                    className="col-span-1 rounded-lg border border-shariti-border bg-white px-3 py-1.5 text-[12px] font-bold focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange"
                />
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || !dirty || montant === ""}
                    data-testid="permanent-remuneration-save"
                    className="col-span-3 md:col-span-1 rounded-full bg-shariti-orange text-white px-4 py-1.5 text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                    {saving ? "…" : "Enregistrer"}
                </button>
            </div>
            <div className="mt-2 text-[10px] text-slate-500 leading-snug">
                {kind === "forfait_mensuel"
                    ? "Montant mensuel brut garanti (€). Ex : 3500€/mois."
                    : "Rémunération à la commission par bulletin net (€/bulletin). Ex : 80€."}
            </div>
        </div>
    );
}


function UsageEditor({ permanent, onSaved }) {
    const [prenom, setPrenom] = useState(permanent.prenom_usage || "");
    const [nom, setNom] = useState(permanent.nom_usage || "");
    const [saving, setSaving] = useState(false);
    const dirty =
        (prenom || "") !== (permanent.prenom_usage || "") ||
        (nom || "") !== (permanent.nom_usage || "");

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/permanents/${permanent.id}`, {
                prenom_usage: prenom.trim() || null,
                nom_usage: nom.trim() || null,
            });
            toast.success("Nom d'usage enregistré");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sauvegarde échouée");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white p-4" data-testid="permanent-usage-block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                Nom d'usage terrain (optionnel)
            </div>
            <div className="text-[11px] text-slate-500 leading-snug mb-3">
                Utilisé pour les plannings et Evergiving. Les documents légaux
                (contrats, PayFit) restent sur le nom légal.
            </div>
            <div className="grid grid-cols-3 gap-2">
                <input
                    type="text"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    placeholder="Prénom d'usage"
                    data-testid="permanent-prenom-usage-input"
                    className="rounded-lg border border-shariti-border bg-white px-3 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange"
                />
                <input
                    type="text"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    placeholder="Nom d'usage"
                    data-testid="permanent-nom-usage-input"
                    className="rounded-lg border border-shariti-border bg-white px-3 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange"
                />
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || !dirty}
                    data-testid="permanent-usage-save"
                    className="rounded-full bg-slate-800 text-white px-4 py-1.5 text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                    {saving ? "…" : "Enregistrer"}
                </button>
            </div>
        </div>
    );
}
